import { MuiThemeProvider, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { SessionService } from '@pbl/pbl-react-core/lib/services';
import { nowUtc } from '@pbl/pbl-react-core/lib/utils/TimeUtil';
import AppSpinner from '@pbl/pbl-react-web-components/lib/app-spinner/AppSpinner';
import styles from 'assets/jss/modules/app/AppLayoutStyle';
import constants, { RemoteConstants } from 'config/constants';
import dayjs from 'dayjs';
import firebase from 'firebase/compat/app';
import 'firebase/compat/remote-config';
import _ from 'lodash';
import OutageScreen from 'modules/outage/outage-screen';
import React, { Fragment } from 'react';
import Confetti from 'react-confetti';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga';
import { connect } from 'react-redux';
import { RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import { IRootState } from 'redux/reducers';
import { getAllCountries } from 'redux/reducers/address/actions';
import { isAuthenticated, loginWithToken, logout } from 'redux/reducers/authentication/actions';
import { displayConfetti } from 'redux/reducers/confetti/actions';
import { getFeatureFlags } from 'redux/reducers/feature/actions';
import { fetchUserPoints, fetchUserTokens } from 'redux/reducers/ledger/actions';
import { loadStorage } from 'redux/reducers/store/actions';
import indexRoutes from 'routes';
import Footer from 'shared/components/footer/Footer';
import AppBar from 'shared/components/menu/AppBar';
import CustomRoute from 'shared/components/routes/CustomRoute';
import getTheme from 'shared/theme/custom';
import { AuthService } from 'utils/auth-service';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
interface IAppLayoutState {
  showOutageBanner: boolean;
  outageConfig: {
    outageType?: 'PLANNED' | 'UNPLANNED';
    flag?: boolean;
    body?: string;
  };
  loadingPoints: boolean;
}
interface IProps extends StateProps, RouteComponentProps, WithStyles<typeof styles>, DispatchProps { }
class AppLayout extends React.Component<IProps, IAppLayoutState> {
  private outageCheck: any | null = null;
  constructor(props) {
    super(props);
    this.state = {
      showOutageBanner: false,
      outageConfig: {
        flag: false,
        body: undefined,
        outageType: undefined
      },
      loadingPoints: false
    };
  }
  public static onRouteWillChange() {
    document.title = constants.DEFAULT_PAGE_TITLE;
  }
  public async componentWillUpdate(nextProps: Readonly<IProps>): Promise<void> {
    if (this.props.location.pathname !== nextProps.location.pathname) {
      AppLayout.onRouteWillChange();
      await this.getData();
    }
  }
  public async componentDidUpdate(previousProps: Readonly<IProps>, _2: Readonly<{}>, _3?: any): Promise<void> {
    if (this.props.location.pathname !== previousProps.location.pathname) {
      const payload = await AuthService.checkSession();
      const useLocalStorageForLogin = firebase.remoteConfig().getBoolean(`USE_LOCAL_STORAGE_TO_LOGIN`);
      if (!!useLocalStorageForLogin && payload) {
        this.triggerLogin(payload);
      }
      await ReactGA.pageview(`${this.props.location.pathname}`);
      await ReactPixel.pageView();
    }
  }
  public updateOutageStatus = async () => {
    await RemoteConstants.sync();
    const outageConfigString = RemoteConstants.getString('OUTAGE_CONFIG');
    let showOutage = false;
    const outageConfig = JSON.parse(outageConfigString);

    if (outageConfig?.flag !== 'true') {
      this.setState({
        showOutageBanner: false,
        outageConfig: {
          flag: false,
          body: undefined,
          outageType: undefined
        }
      });
      return;
    }

    const now = nowUtc().unix();
    const startDate = dayjs.utc(outageConfig.startDate).unix();
    const isValidStartDate = now >= startDate;

    if (isValidStartDate) {
      if (!!outageConfig.endDate) {
        const endDate = dayjs.utc(outageConfig.endDate).unix();
        const isValidEndDate = now <= endDate;
        if (isValidEndDate) {
          showOutage = true;
        }
      } else {
        showOutage = true;
      }
    }
    this.setState({
      showOutageBanner: showOutage,
      outageConfig
    });
  };
  public async componentDidMount() {
    try {
      await SessionService.status();
    } catch (e) {
      console.warn(e);
    }
    const useLocalStorageForLogin = RemoteConstants.getBoolean(`USE_LOCAL_STORAGE_TO_LOGIN`);
    this.outageCheck = setInterval(this.updateOutageStatus, 300000);
    const payload = await AuthService.checkSession();

    if (!!useLocalStorageForLogin && payload) {
      this.triggerLogin(payload);
    }
    await this.getData();
    await this.updateOutageStatus();
    await this.props.getAllCountries();
    await ReactGA.pageview(`${this.props.location.pathname}`);
    await ReactPixel.pageView();
  }
  public componentWillUnmount = async () => {
    if (!!this.outageCheck) {
      clearInterval(this.outageCheck);
    }
    this.outageCheck = null;
  };
  public triggerLogin = (payload: { accessToken: string; refreshToken?: string; userId: string }) => {
    const isValidPayload =
      !!payload && !!payload.accessToken?.trim().length && !!payload.accessToken?.trim().length && !!payload.userId?.trim().length;
    if (isValidPayload && (!this.props.isLoggedIn || payload.userId !== this.props.userId)) {
      this.props.loginWithToken(
        payload.accessToken,
        payload.userId,
        payload.refreshToken,
        () => { },
        () => { }
      );
    }
  };
  public getData = async (): Promise<void> => {
    await this.setState({ loadingPoints: true });
    await this.props.getFeatureFlags();
    await this.props.loadStorage();
    await this.props.isAuthenticated();
    if (this.props.isLoggedIn) {
      // Fetching both tokens & points on page refresh
      await this.props.fetchUserTokens();
      await this.props.fetchUserPoints();

      await this.setState({ loadingPoints: false });
      // exponea
      // @ts-ignore
      if (window.exponea && this.props.userId) {
        // @ts-ignore
        window.exponea.identify({ user_key: this.props.userId }, this.props.email ? { email: this.props.email } : null);
      }
    }
  };
  public render(): React.ReactNode {
    const { isLoggedIn, userPoints, userTokens, userFullName, authorizedFlags, loadingFlags, storeItems } = this.props;
    const theme = (window.location.pathname.includes('lucky-lounge') ? getTheme('sweepstakesTheme') : getTheme()) as Partial<Theme>;
    const imageStyles =
      (theme?.palette?.background as any).image &&
        !window.location.pathname.includes('lucky-lounge/game') &&
        !window.location.pathname.includes('lucky-lounge/explore')
        ? {
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundImage: `url(${(theme?.palette?.background as any).image})`
        }
        : {};
    const widget = window.location.pathname.includes('games-quick-draw-widget') || window.location.pathname.includes('store/pay');
    if (widget) {
      return (
        <Fragment>
          <Switch>
            {indexRoutes.map((prop, key) => (
              <CustomRoute key={key} {...prop} />
            ))}
          </Switch>
        </Fragment>
      );
    }
    if (this.state.showOutageBanner) {
      return (
        <Fragment>
          <OutageScreen
            outageType={this.state.outageConfig.outageType as 'PLANNED' | 'UNPLANNED'}
            flag={this.state.showOutageBanner}
            body={this.state.outageConfig.body as string}
          />
        </Fragment>
      );
    }
    return (
      <Fragment>
        <AppBar
          isAuthenticated={isLoggedIn}
          userPoints={userPoints}
          userTokens={userTokens}
          userFullName={userFullName}
          onLogout={this.onLogout}
          showMessage={!!this.props.showMessage}
          authorizedFlags={authorizedFlags}
          loadingFlags={loadingFlags}
          storeItems={storeItems}
          loadingPoints={this.state.loadingPoints}
        />
        {this.props.confetti.showConfetti && (
          <Confetti
            style={{ zIndex: 1301 }}
            tweenDuration={3000}
            numberOfPieces={1500}
            width={window.innerWidth}
            height={document.body.scrollHeight}
          />
        )}
        <MuiThemeProvider theme={theme}>
          <div
            id="pageScrollTarget"
            className={this.props.classes.root}
            style={{ background: theme?.palette?.background.default, ...imageStyles }}
          >
            <section className={this.props.classes.page} id={'main'} tabIndex={-1}>
              <Switch>
                {indexRoutes.map((prop, key) => (
                  <CustomRoute key={key} {...prop} />
                ))}
              </Switch>
            </section>
            <Footer isAuthenticated={isLoggedIn} />
          </div>
        </MuiThemeProvider>
        {this.props.spinning && <AppSpinner label={this.props.spinnerLabel} />}
      </Fragment>
    );
  }
  private onLogout = () => {
    this.props.logout();
    this.props.history.push('/');
  };
}
const mapStateToProps = ({
  app: { showMessage, spinning, label },
  ledger: { userTokens, userBalance },
  authentication: { accessToken, account },
  feature: { authorizedFlags, loading },
  store: { selectedItems },
  confetti
}: IRootState) => ({
  showMessage,
  spinning,
  spinnerLabel: label,
  userPoints: userBalance?.balance,
  userTokens,
  userFullName: account ? `${account.firstName} ${account.lastName}` : '',
  userId: account?.login as string | undefined,
  isLoggedIn: !!accessToken && accessToken.length > 0 && !!account && !!account.email,
  authorizedFlags,
  loadingFlags: loading,
  storeItems: _.sumBy(selectedItems, 'quantity'),
  confetti
});
const mapDispatchToProps = {
  fetchUserPoints,
  fetchUserTokens,
  loginWithToken,
  logout,
  isAuthenticated,
  getAllCountries,
  getFeatureFlags,
  loadStorage,
  displayConfetti
};
export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
    null,
    { pure: false }
    // @ts-ignore
  )(withStyles(styles)(AppLayout))
);
